import { action, computed, observable } from 'mobx'
import { debounce, sortBy } from 'lodash-es'
import { tap } from 'rxjs'
import {
  analyze,
  createLexer,
  createParser,
  type Diagnostic,
  hasErrors,
  type Program,
  type RynoType,
  SymbolTable,
  unify,
  Variance,
} from '@taxfyle/ryno'
import { Types } from '@/features/legends/editor/ryno-types'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { toStream } from '@/utils/mobx-util'
import { teardown } from '@/utils/rx-util'

/**
 * A formula problem. Diagnostics from Ryno carry a location; the "required"
 * and result type problems don't.
 */
export interface RynoDiagnostic {
  severity: 'error' | 'warning'
  message: string
  startPos?: number
  endPos?: number
  loc?: Diagnostic['loc']
}

/**
 * An identifier in scope, offered by autocompletion.
 */
export interface RynoCompletion {
  name: string
  type: RynoType
}

export interface RynoEditorViewModelOptions {
  getSymbolTable: () => SymbolTable
  getNeededType?: () => RynoType | null | undefined
  required?: boolean | (() => boolean)
}

/**
 * A Ryno formula and its analysis. While active, the formula is analyzed when
 * it changes and when what it is checked against changes.
 */
export class RynoEditorViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _isRequired: () => boolean
  private readonly _getSymbolTable: () => SymbolTable
  private readonly _getNeededType: () => RynoType | null | undefined
  private readonly _scheduleAnalyze = debounce(() => this.analyze(), 400)

  @observable.ref private accessor _lastUsedSymbolTable: SymbolTable | null =
    null

  @observable accessor value = ''
  @observable.ref accessor diagnostics: RynoDiagnostic[] = []

  /**
   * The formula's last expression. Info line rows show it as a summary.
   */
  @observable accessor lastLine = ''

  private readonly _changes$ = toStream(() => [
    this.value,
    this._getNeededType(),
    this._getSymbolTable(),
  ]).pipe(
    tap(() => this._scheduleAnalyze()),
    teardown(() => this._scheduleAnalyze.cancel()),
  )

  constructor({
    getSymbolTable,
    getNeededType,
    required,
  }: RynoEditorViewModelOptions) {
    this._isRequired =
      typeof required === 'function' ? required : () => required === true
    this._getSymbolTable = getSymbolTable
    this._getNeededType = getNeededType ?? (() => null)
    this._lifecycle.manage(this._changes$)
  }

  /**
   * The diagnostics in the order they appear in the formula.
   */
  @computed
  get sortedDiagnostics() {
    return sortBy(this.diagnostics, (d) => d.startPos ?? -1)
  }

  /**
   * Whether the last analysis found errors.
   */
  @computed
  get hasErrors() {
    return this.diagnostics.some((d) => d.severity === 'error')
  }

  /**
   * Identifiers in scope, for autocompletion.
   */
  @computed
  get autocompleteList(): RynoCompletion[] {
    const table = this._lastUsedSymbolTable ?? this._getSymbolTable()
    const symbols = table
      .listIdentifierSymbols()
      .filter((symbol) => !!symbol.name)
      .map((symbol) => ({ name: symbol.name, type: symbol.type }))
    return sortBy(symbols, (x) => x.name.toLowerCase())
  }

  /**
   * Analyzes the formula right away, so it shows its diagnostics before it
   * is edited.
   */
  @action.bound
  activate() {
    this._lifecycle.activate()
    this.analyze()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Analyzes the formula and stores the diagnostics.
   *
   * @returns Whether it has no errors.
   */
  @action.bound
  analyze(): boolean {
    if (!this.value) {
      this._lastUsedSymbolTable = null
      if (this._isRequired()) {
        this.diagnostics = [
          { severity: 'error', message: 'This formula is required' },
        ]
        return false
      }
      this.diagnostics = []
      return true
    }

    const ast = this._parseSource()
    this._setLastLine(ast)
    const table = new SymbolTable('sub', 1, this._getSymbolTable())
    this._lastUsedSymbolTable = table
    const result = analyze(ast, table)

    const diagnostics: RynoDiagnostic[] = [
      ...ast.diagnostics,
      ...result.diagnostics,
    ]
    const neededType = this._getNeededType()
    if (neededType && !diagnostics.some((d) => d.severity === 'error')) {
      const unifyResult = unify(
        result.type ?? Types.nothing,
        neededType,
        table,
        Variance.Covariant,
      )
      if (hasErrors(unifyResult)) {
        diagnostics.push(
          ...(unifyResult.errors ?? []).map((m) => ({
            severity: 'error' as const,
            message: `Incompatible result type: ${String(m)}`,
          })),
        )
      }
    }
    this.diagnostics = diagnostics
    return !diagnostics.some((x) => x.severity === 'error')
  }

  /**
   * Sets the formula.
   */
  @action.bound
  setValue(value: string | null | undefined) {
    this.value = value ?? ''
  }

  /**
   * The formula as the engine expects it.
   */
  toJSON() {
    return this._getFixedValue()
  }

  private _parseSource(): Program {
    return createParser(createLexer(this._getFixedValue())).parseProgram()
  }

  /**
   * Tabs are replaced with spaces, as the engine expects.
   */
  private _getFixedValue() {
    return this.value.replace(/\t/g, '  ')
  }

  private _setLastLine(ast: Program) {
    if (ast.expressions.length === 0) {
      this.lastLine = ''
      return
    }
    if (ast.diagnostics.some((d) => d.severity === 'error')) {
      this.lastLine = `(there's a syntax error in your formula)`
      return
    }
    const lastExpr = ast.expressions[ast.expressions.length - 1]
    this.lastLine = this._getFixedValue().substring(lastExpr.startPos)
  }
}
